<!--
	FaqItem — native <details>/<summary>, no JS accordion. The question is a real
	heading inside the summary so it counts for SEO (each phrased as the query,
	plan/08 §4) while staying a keyboard-operable disclosure. The +/− mark is pure CSS.

	The open/close glide uses `::details-content` + `interpolate-size`, which is a
	progressive enhancement: where it isn't supported the disclosure simply snaps,
	which is the native behaviour and perfectly fine. No JS height measuring.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';

	let {
		question,
		children,
		open = false,
		level = 3
	}: {
		question: string;
		children: Snippet;
		open?: boolean;
		/** Heading level so pages avoid heading-order skips (2 on /faq, 3 inline). */
		level?: 2 | 3;
	} = $props();
</script>

<details class="faq" {open}>
	<summary class="faq__summary">
		<svelte:element this={`h${level}`} class="faq__q">{question}</svelte:element>
		<span class="faq__mark" aria-hidden="true"></span>
	</summary>
	<div class="faq__a">
		{@render children()}
	</div>
</details>

<style>
	.faq {
		border-bottom: 1px solid var(--color-line);
	}
	.faq__summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 1.375rem 0;
		cursor: pointer;
		list-style: none;
	}
	.faq__summary::-webkit-details-marker {
		display: none;
	}
	.faq__q {
		font-family: var(--font-sans);
		font-size: var(--text-h3);
		font-weight: 500;
		color: var(--color-ink-900);
		margin: 0;
		transition: color var(--dur-fast) var(--ease-out);
	}
	.faq__summary:hover .faq__q {
		color: var(--color-cut-700);
	}

	/* +/− drawn from two 1px bars; the vertical one rotates away when open. */
	.faq__mark {
		position: relative;
		flex: none;
		width: 14px;
		height: 14px;
	}
	.faq__mark::before,
	.faq__mark::after {
		content: '';
		position: absolute;
		inset: 50% 0 auto 0;
		height: 1px;
		background-color: var(--color-cut-600);
		transition: transform var(--dur-med) var(--ease-out);
	}
	.faq__mark::after {
		transform: rotate(90deg);
	}
	.faq[open] .faq__mark::after {
		transform: rotate(0deg);
	}

	.faq__a {
		padding: 0 0 1.5rem;
		max-width: var(--container-measure);
		color: var(--color-ink-500);
	}
	.faq__a :global(a) {
		color: var(--color-cut-700);
		text-decoration: underline;
		text-underline-offset: 0.15em;
	}

	/* Progressive enhancement: glide the disclosure open where the UA can size it. */
	@supports (interpolate-size: allow-keywords) {
		.faq {
			interpolate-size: allow-keywords;
		}
		.faq::details-content {
			block-size: 0;
			overflow: hidden;
			opacity: 0;
			transition:
				block-size var(--dur-slow) var(--ease-out),
				content-visibility var(--dur-slow) var(--ease-out) allow-discrete,
				opacity var(--dur-slow) var(--ease-out);
		}
		.faq[open]::details-content {
			block-size: auto;
			opacity: 1;
		}
	}

	:global(.sec--dark) .faq {
		border-color: var(--color-app-700);
	}
	:global(.sec--dark) .faq__q {
		color: var(--color-bone-100);
	}
	:global(.sec--dark) .faq__a {
		color: var(--color-bone-300);
	}
	:global(.sec--dark) .faq__mark::before,
	:global(.sec--dark) .faq__mark::after {
		background-color: var(--color-cut-400);
	}
</style>
